import { AiEntityDescriptor } from "./entities-description"

/**
 * AI Adapter Protocol — FE <-> Chrome extension (`window.postMessage`)
 *
 * Conversation model
 * - One conversation per UI entity, keyed by `conversationId`.
 * - Format: `{entity}:{id}` — examples:
 *     claim:550e8400-e29b-41d4-a716-446655440000
 *     claimSummary:550e8400-e29b-41d4-a716-446655440000
 *     message:7c9e6679-7425-40de-944b-e07fc1f90ae7
 *     patientStatement:patient-123
 * - Extension owns persistence. FE never resends prior turns.
 * - First `chat` for a conversationId creates the conversation lazily.
 * - `history` returns the full thread (or empty messages if none yet).
 *
 * Transport
 * - FE -> extension: `AiRequest` with `type: "AI_REQUEST"`
 * - Extension -> FE: `AiResponse` with `type: "AI_RESPONSE"`
 * - Every response MUST echo the request's `requestId` and `action`.
 * - Origin: same-origin `postMessage` (FE validates `event.origin`).
 *
 * Extension responsibilities
 * history:
 *   1. Look up conversation by `conversationId`.
 *   2. On miss -> success: true, data.messages = [] (do NOT error).
 *   3. On hit  -> success: true, data = full AiConversation (oldest -> newest).
 *
 * chat:
 *   1. Load-or-create conversation for `conversationId`.
 *   2. Append user message; run model with prior turns + this turn's context.
 *   3. Append assistant message; return AiChatResult (not only a bare string).
 *   4. Persist context from this turn for follow-ups when FE omits heavy fields
 *      (`dataJson` / `file`) on later turns — see AiContext notes.
 *
 * Errors: success: false + error string; omit data.
 */

/** Well-known entity prefixes for `conversationId` (`{entity}:{id}`). */
export type AiConversationEntity =
  | "claim"
  | "claimSummary"
  | "message"
  | "patientStatement"
  | "connectionCheck"

/**
 * Durable conversation key. Must be `{entity}:{id}`.
 * Use `buildAiConversationId` on the FE when constructing requests.
 */
export type AiConversationId = `${AiConversationEntity}:${string}` | (string & {})

/** Build a namespaced conversation id, e.g. `claim:abc-123`. */
export function buildAiConversationId(entity: AiConversationEntity, id: string): AiConversationId {
  return `${entity}:${id}`
}

/** Model task for a chat turn (formerly top-level `action` on the old one-shot protocol). */
export type AiTask = "summarize" | "verify" | "explain" | "suggest"

export type AiFileAttachment = {
  name: string
  type: string
  size: number
  /** Binary file bytes (ArrayBuffer-backed). Large — send only when needed. */
  content: Uint8Array
}

/**
 * Entity snapshot for the model.
 *
 * - Always send `entity`.
 * - Send `dataJson` / `file` on the first chat turn of a conversation,
 *   and again whenever the snapshot or attachment changes.
 * - On later turns, FE may omit `dataJson` / `file`; extension should reuse
 *   the last stored context for that conversationId.
 */
export type AiContext = {
  entity: AiEntityDescriptor
  /** JSON snapshot of the entity / related data. */
  dataJson?: string
  file?: AiFileAttachment
}

export type AiMessage = {
  /** Stable id assigned by the extension (UUID). */
  id: string
  role: "user" | "assistant"
  content: string
  /** ISO-8601 timestamp. */
  createdAt: string
  /** Task that produced this assistant turn (omit for user messages). */
  task?: AiTask
}

export type AiConversation = {
  /** Namespaced key, e.g. `claim:…` / `message:…`. */
  conversationId: AiConversationId
  /** Chronological order, oldest first. Empty array if no messages yet. */
  messages: AiMessage[]
}

export type AiChatResult = {
  conversationId: AiConversationId
  /** Assistant message appended for this turn. */
  assistantMessage: AiMessage
}

export type AiRequest =
  | {
      type: "AI_REQUEST"
      action: "history"
      requestId: string
      /** Durable conversation key — `{entity}:{id}`. */
      conversationId: AiConversationId
    }
  | {
      type: "AI_REQUEST"
      action: "chat"
      requestId: string
      conversationId: AiConversationId
      task: AiTask
      context: AiContext
      userMessage: string
      /** Page / workflow hint (what the user is doing). */
      utility: string
    }

export type AiResponse =
  | {
      type: "AI_RESPONSE"
      action: "history"
      requestId: string
      success: boolean
      /** Present when success. Use messages: [] when no thread exists yet. */
      data?: AiConversation
      error?: string
    }
  | {
      type: "AI_RESPONSE"
      action: "chat"
      requestId: string
      success: boolean
      data?: AiChatResult
      error?: string
    }
